import { cn } from "@guru/shared/utils/cn";
import { Check, CheckCheck } from "lucide-react";
import type React from "react";

export type TickState = "sent" | "delivered" | "read";

/** WhatsApp-style delivery ticks: single grey → double grey → double blue. */
export function Ticks({ state, className }: { state: TickState; className?: string }) {
  const Icon = state === "sent" ? Check : CheckCheck;
  return (
    <Icon
      aria-hidden="true"
      className={cn(
        "inline h-3.5 w-3.5 shrink-0",
        state === "read" ? "text-sky-500" : "text-muted-foreground/70",
        className
      )}
    />
  );
}

interface ChatBubbleProps {
  direction: "in" | "out";
  time?: string;
  ticks?: TickState;
  className?: string;
  children: React.ReactNode;
}

/**
 * WhatsApp-style chat bubble. Colours deliberately mimic WhatsApp (it is
 * depicting WhatsApp, not a theme surface).
 */
export function ChatBubble({ direction, time, ticks, className, children }: ChatBubbleProps) {
  return (
    <div className={cn("flex w-full", direction === "out" ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "relative max-w-[85%] rounded-lg px-3 py-2 text-[13px] leading-snug shadow-sm",
          direction === "out"
            ? "rounded-tr-none bg-[#d9fdd3] text-[#111b21] dark:bg-[#005c4b] dark:text-[#e9edef]"
            : "rounded-tl-none bg-white text-[#111b21] dark:bg-[#202c33] dark:text-[#e9edef]",
          className
        )}
      >
        {children}
        {(time || ticks) && (
          <span className="float-right ml-2 mt-1.5 flex items-center gap-1 text-[10px] text-[#667781] dark:text-[#8696a0]">
            {time}
            {direction === "out" && ticks && <Ticks state={ticks} />}
          </span>
        )}
      </div>
    </div>
  );
}

interface ChatShellProps {
  title: string;
  subtitle?: string;
  className?: string;
  children: React.ReactNode;
}

/** WhatsApp-style conversation surface: header + wallpaper + message column. */
export function ChatShell({ title, subtitle = "online", className, children }: ChatShellProps) {
  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="flex items-center gap-2.5 bg-[#008069] px-3 pb-2.5 pt-7 text-white dark:bg-[#202c33]">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-full bg-white/25 text-xs font-bold"
        >
          {title.charAt(0)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-tight">{title}</span>
          <span className="block text-[11px] leading-tight text-white/80">{subtitle}</span>
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-hidden bg-[#efeae2] p-3 dark:bg-[#0b141a]">
        {children}
      </div>
    </div>
  );
}
